import React, { useState } from "react";
import { Scatter } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Text,
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Select,
  VStack,
  HStack,
} from "@chakra-ui/react";
import StepHeader from "../../../../../../components/StepperHeader";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/reducer/rootReducer";
import { AppDispatch } from "../../../../../../redux/store";
import { saveFormData } from "../../../../../../redux/slice/formSlice";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  annotationPlugin
);

interface FocusingMatrixProps {
  onNext: () => void;
  onPrev: () => void;
  onSave: () => void;

  loading: boolean;
}

interface MatrixRowType {
  id: number;
  name: string;
  importance: number;
  urgency: number;
  tableDisplay: boolean;
  color: string;
}

const FocusingMatrix = (props: FocusingMatrixProps) => {
  const changeIdeaData = useSelector(
    (state: RootState) => state.form.formData["7"]
  );
  const dispatch: AppDispatch = useDispatch();

  

  console.log("changeIdeaData", Object.values(changeIdeaData));

  const mappedChangeIdeas = Object.values(changeIdeaData).flatMap((item: any) =>
    Object.values(item.changeIdea).map((changeIdea: any, index: number) => ({
      id: index + 1,
      name: changeIdea,
      importance: 0,
      urgency: 0,
      tableDisplay: false,
      color: `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(
        Math.random() * 256
      )}, ${Math.floor(Math.random() * 256)}, 1)`,
    }))
  );

   
  const [tasks, setTasks] = useState<MatrixRowType[]>(mappedChangeIdeas
  );


  const updateTask = (
    id: number,
    key: "importance" | "urgency",
    value: number
  ) => {
    setTasks(
      tasks.map((task) => (task.id === id ? { ...task, [key]: value } : task))
    );
  };

  // chart configurations

 
  const chartData = {
    datasets: tasks.map((task, index)=> {
      return {
        id: index,
        label: task.name,
        data: [{ x: task.importance, y: task.urgency }],
        backgroundColor: task.color,
        pointRadius: 8,
      }
    })
  };

  const options = {
    scales: {
      x: {
        title: {
          display: true,
          text: "Importance",
        },
        min: -5,
        max: 5,
      },
      y: {
        title: {
          display: true,
          text: "Ease Of Implementation",
        },
        min: -5,
        max: 5,
      },
    },
    plugins: {
      annotation: {
        annotations: {
          // Vertical line at x=0
          verticalLine: {
            type: "line",
            xMin: 0,
            xMax: 0,
            borderColor: "black",
            borderWidth: 2,
          },
          // Horizontal line at y=0
          horizontalLine: {
            type: "line",
            yMin: 0,
            yMax: 0,
            borderColor: "black",
            borderWidth: 2,
          },
          // quadrant texts
          quadrant1Text: {
            type: "label",
            xValue: 5,
            yValue: 8,
            content: ["Important & Easy"],
            backgroundColor: "rgba(0,0,0,0.0)",
            color: "black",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          quadrant2Text: {
            type: "label",
            xValue: -5,
            yValue: 8,
            content: ["Not Important & Easy"],
            backgroundColor: "rgba(0,0,0,0.0)",
            color: "black",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          quadrant3Text: {
            type: "label",
            xValue: 5,
            yValue: -8,
            content: ["Important & Not Easy"],
            backgroundColor: "rgba(0,0,0,0.0)",
            color: "black",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          quadrant4Text: {
            type: "label",
            xValue: -5,
            yValue: -8,
            content: ["Not Important & Not Easy"],
            backgroundColor: "rgba(0,0,0,0.0)",
            color: "black",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            return context.raw.label;
          },
        },
      },
    },
  };

  return (
    <VStack h="100%" w="100%" spacing={4}>
      <StepHeader
        title="Step 5: Focusing Matrix"
        description="Now that we have identified our root causes, we will now prioritize our tasks based on their importance and urgency. This will help us focus on the most important and urgent tasks first."
      />
      <Box w={"100%"} display={"flex"} gap={10} flexDirection={"row"}>
        <Box w={"40%"}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th width="40%">Name</Th>
                <Th width="30%">Ease Of Implementation</Th>
                <Th width="30%">Importance</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tasks
                .filter((x) => !x.tableDisplay)
                .map((row) => (
                  <Tr key={`task-${row.id}`}>
                    <Td>{row.name}</Td>
                    <Td>
                      <Select
                        value={row.urgency}
                        onChange={(e) =>
                          updateTask(row.id, "urgency", Number(e.target.value))
                        }
                      >
                        {Array.from({ length: 11 }, (_, i) => i - 5).map(
                          (value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          )
                        )}
                      </Select>
                    </Td>
                    <Td>
                      <Select
                        value={row.importance}
                        onChange={(e) =>
                          updateTask(
                            row.id,
                            "importance",
                            Number(e.target.value)
                          )
                        }
                      >
                        {Array.from({ length: 11 }, (_, i) => i - 5).map(
                          (value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          )
                        )}
                      </Select>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
          <Text mt={4} fontSize="sm" color="gray.600" textAlign="center">
            * Ease of implementation and Importance are on a scale from -5 to 5
          </Text>
        </Box>
        <Box  w={"60%"} h={"100%"}>
          <Scatter data={chartData} options={options as any} />
        </Box>
      </Box>
      <HStack
        flex={0.1}
        w="100%"
        justifyContent="space-between"
        p={4}
        bg="gray.50"
      >
        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<ArrowLeftIcon />}
          onClick={() => {
            props.onPrev();
          }}
        >
          Previous
        </Button>
        <HStack>
          <Button colorScheme="blue" variant="outline" onClick={() => {}}>
            Save & Exit
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => {
              dispatch(saveFormData({ step: "8", data: tasks }));
              props.onNext();
            }}
            w={"50%"}
            rightIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default FocusingMatrix;
